import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  DPIA_COVERAGE_STATUSES,
  DPIA_COVERAGE_STATUS_LABELS,
  getCollection,
  type DpiaCoverage,
  type DpiaCoverageItem,
  type DpiaCoverageStatus,
} from '@grc/types';
import { apiFetch, ApiRequestError } from '../../lib/api.js';
import { useOrg } from '../../lib/auth.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  LoadingPane,
  PageHeader,
  Table,
  formatDate,
  type BadgeTone,
} from '../../components/ui.js';
import { RecordWizard } from './RecordWizard.js';
import './records.css';

const STATUS_TONE: Record<DpiaCoverageStatus, BadgeTone> = {
  covered: 'success',
  pending: 'info',
  missing: 'danger',
  review_overdue: 'warning',
};

const COUNT_KEY: Record<DpiaCoverageStatus, keyof DpiaCoverage['counts']> = {
  covered: 'covered',
  pending: 'pending',
  missing: 'missing',
  review_overdue: 'reviewOverdue',
};

const TYPE_LABEL: Record<DpiaCoverageItem['collection'], string> = {
  software: 'Software',
  suppliers: 'Supplier',
};

const EMPTY: DpiaCoverage = {
  items: [],
  counts: { covered: 0, pending: 0, missing: 0, reviewOverdue: 0, total: 0 },
};

/** DPIA coverage per cloud service (/registers/dpias/coverage). */
export function DpiaCoveragePage() {
  const { org } = useOrg();
  const canWrite = org.role === 'admin' || org.role === 'contributor';
  const dpiaDef = getCollection('dpias')!;

  // Keyed under ['records', orgId] so the wizard's prefix invalidation
  // refetches this view after "Create DPIA".
  const coverage = useQuery({
    queryKey: ['records', org.orgId, 'dpia-coverage'],
    queryFn: () => apiFetch<DpiaCoverage>(`/orgs/${org.orgId}/dpia-coverage`),
  });
  const [createFor, setCreateFor] = useState<DpiaCoverageItem | null>(null);

  if (coverage.isLoading) return <LoadingPane label="Loading DPIA coverage" />;
  if (coverage.isError) {
    const forbidden = coverage.error instanceof ApiRequestError && coverage.error.status === 403;
    return (
      <div>
        <PageHeader title="DPIA coverage" />
        <EmptyState
          title="Could not load DPIA coverage"
          message={
            forbidden
              ? 'DPIA coverage is available to the organisation’s own members.'
              : 'Something went wrong — please try again shortly.'
          }
          action={
            !forbidden && (
              <Button variant="secondary" onClick={() => void coverage.refetch()}>
                Retry
              </Button>
            )
          }
        />
      </div>
    );
  }
  const { items, counts } = coverage.data ?? EMPTY;

  return (
    <div>
      <PageHeader
        title="DPIA coverage"
        subtitle="Every cloud service should have a data protection impact assessment recording where the data lives and who can reach it"
      />

      {items.length === 0 ? (
        <EmptyState
          title="No cloud services recorded yet"
          message="Tick “Cloud service?” on a software item or supplier and it will be listed here with the DPIA that covers it."
          action={
            <div className="dialog-actions" style={{ justifyContent: 'center' }}>
              <Link to="/assets/software">
                <Button variant="secondary">Software</Button>
              </Link>
              <Link to="/assets/suppliers">
                <Button variant="secondary">Suppliers</Button>
              </Link>
            </div>
          }
        />
      ) : (
        <>
          <div className="coverage-tiles">
            {DPIA_COVERAGE_STATUSES.map((status) => (
              <Card key={status} className={`coverage-tile ${status}`}>
                <div className="coverage-tile-value">{counts[COUNT_KEY[status]]}</div>
                <div className="coverage-tile-label">{DPIA_COVERAGE_STATUS_LABELS[status]}</div>
              </Card>
            ))}
          </div>
          <p className="coverage-hint">
            {counts.total} cloud service{counts.total === 1 ? '' : 's'} in scope. A service whose
            DPIA was rejected counts as missing until a new DPIA is approved; an approved DPIA
            whose next review date has passed shows as review overdue.
          </p>

          <Table
            head={
              <>
                <th>Service</th>
                <th>Type</th>
                <th>Data location</th>
                <th>Internal access</th>
                <th>External access</th>
                <th>DPIA</th>
                <th>Status</th>
                <th />
              </>
            }
          >
            {items.map((item) => (
              <tr key={item.recordId}>
                <td style={{ fontWeight: 600 }}>{item.name}</td>
                <td>{TYPE_LABEL[item.collection]}</td>
                <td>
                  {item.dataLocation.length > 0 ? item.dataLocation.join(', ') : '—'}
                  {item.dataLocationDetail && (
                    <span className="coverage-detail">{item.dataLocationDetail}</span>
                  )}
                </td>
                <td>{item.accessInternal || '—'}</td>
                <td>{item.accessExternal || '—'}</td>
                <td>
                  {item.dpiaRecordId ? (
                    <>
                      {item.dpiaName}
                      <span className="coverage-detail">
                        {item.dpiaOutcome ?? 'No outcome yet'}
                        {item.dpiaReviewDate && ` · review ${formatDate(item.dpiaReviewDate)}`}
                      </span>
                    </>
                  ) : (
                    '—'
                  )}
                </td>
                <td>
                  <Badge tone={STATUS_TONE[item.status]}>
                    {DPIA_COVERAGE_STATUS_LABELS[item.status]}
                  </Badge>
                </td>
                <td>
                  <div className="record-row-actions">
                    {item.dpiaRecordId ? (
                      <Link to={`/registers/dpias?record=${encodeURIComponent(item.dpiaRecordId)}`}>
                        <Button variant="secondary" size="sm">
                          Open DPIA
                        </Button>
                      </Link>
                    ) : (
                      canWrite && (
                        <Button size="sm" onClick={() => setCreateFor(item)}>
                          Create DPIA
                        </Button>
                      )
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </Table>
        </>
      )}

      <RecordWizard
        def={dpiaDef}
        record={null}
        open={createFor !== null}
        onClose={() => setCreateFor(null)}
        initialData={
          createFor ? { name: `${createFor.name} DPIA`, service: createFor.recordId } : undefined
        }
      />
    </div>
  );
}
